import { useContext, useEffect, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { RoleDto } from 'api/Identity/Types/Role'
import AddButton from 'components/AddButton/AddButton'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import MoreButton from 'components/MoreButton/MoreButton'
import { Spinner } from 'components/Spinner'
import { errorToast, successToast, ToastContext } from 'components/Toast/Toast'
import { useAppDispatch, useAppSelector } from 'store'
import { pageableSettings } from 'store/grid/grid.slice'
import { fetchPermissionsAction } from 'store/identity/identity.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { IdentityApiClient } from 'utils/api/identityApi'
import { PartnershipsApiClient } from 'utils/api/partnershipsApi'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { appENV } from 'utils/dom'
import { isEnabledOnEnvs, links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import stringComparator from 'utils/stringComparator'

import ClientAreaAuditLogModal from '../../../components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import TitleSearch from '../Users/TitleSearch'
import { AddRoleForm, AddRoleFormValues } from './AddRoleForm'
import { EditRoleFormValues } from './EditRoleGeneralForm'
import { EditRolePage, EditRoleTab } from './EditRolePage'
import { AddRolePermissionFormValues } from './EditRolePermissionForm'
import roleColumns from './roleColumns'

const RolesPage = (): JSX.Element => {
  const identityApiClient = useApiClient(IdentityApiClient)
  const clientsApiClient = useApiClient(ClientsApiClient)
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const partnershipsApiClient = useApiClient(PartnershipsApiClient)

  const [role, setRole] = useState<RoleDto | undefined>(undefined)
  const [roles, setRoles] = useState<RoleDto[]>([])
  const [dataState, setDataState] = useState<{ skip: number; take: number }>(
    getPaginationInitialState()
  )
  const [isAddRoleModal, setAddRoleModal] = useState<boolean>(false)
  const [editRoleModal, setEditRoleModal] = useState<boolean>(false)
  const [auditLogModal, setAuditLogModal] = useState<{ visible: boolean; data?: RoleDto }>({
    visible: false,
  })
  const [roleIdToDelete, setRoleIdToDelete] = useState<string | null>(null)

  const permissions = useAppSelector((state) => state.identity.permissions)
  const dispatch = useAppDispatch()

  const setToast = useContext(ToastContext)

  const { callback, forceRefresh } = useCallbackWithForceRefresh(
    () => identityApiClient.fetchRoles(),
    []
  )
  const { data, isLoading } = useFetchOne(callback)

  const setSecuredPropertiesMap = {
    client: clientsApiClient.setRoleClientsSecuredProperties,
    trading_account: tradingAccountsApiClient.setRoleTradingAccountsSecuredProperties,
    introducing_broker: partnershipsApiClient.setRolePartnershipsSecuredProperties,
  }

  useEffect(() => {
    if (data) {
      setRoles(sortRoles(data))
    }
    if (isEmpty(permissions)) dispatch(fetchPermissionsAction(identityApiClient))
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps

  const addRoleModal = () => {
    setRole(undefined)
    setAddRoleModal(true)
  }

  const setAuditLog = (selectedRole: RoleDto) => {
    setAuditLogModal({ data: selectedRole, visible: true })
  }

  const handleSearch = (searchText: string) => {
    if (!data) return

    const lowercasedSearchText = searchText.toLowerCase()

    const sortPartialMatches = (a: RoleDto, b: RoleDto) => {
      const aName = a.name.toLowerCase()
      const bName = b.name.toLowerCase()
      const aStartsWith = aName.startsWith(lowercasedSearchText)
      const bStartsWith = bName.startsWith(lowercasedSearchText)
      if (aStartsWith && !bStartsWith) return -1
      if (!aStartsWith && bStartsWith) return 1
      if (aStartsWith && bStartsWith) return a.name.length - b.name.length
      return aName.localeCompare(bName)
    }

    const exactMatches = data.filter(
      ({ name }: RoleDto) => name.toLowerCase() === lowercasedSearchText
    )

    const partialMatches = data
      .filter(({ name }: RoleDto) => {
        const lowercasedName = name.toLowerCase()
        return (
          lowercasedName.includes(lowercasedSearchText) && lowercasedName !== lowercasedSearchText
        )
      })
      .sort(sortPartialMatches)

    const permissionMatches = data.filter(
      ({ permissions, name }: RoleDto) =>
        permissions.some((p: PermissionDto) =>
          p.name.toLowerCase().includes(lowercasedSearchText)
        ) &&
        !exactMatches.some((r) => r.name === name) &&
        !partialMatches.some((r) => r.name === name)
    )

    const sortedResults = [...exactMatches, ...partialMatches, ...permissionMatches]
    setRoles(sortedResults)
    setDataState({ ...dataState, skip: 0 })
  }

  const pageChange = (event: GridPageChangeEvent) => {
    if (data) {
      setDataState({ skip: event.page.skip, take: event.page.take })
    }
  }

  const handleEdit = (roleItem: RoleDto) => {
    setRole(roleItem)
    setEditRoleModal(true)
  }

  const handleClone = (roleItem: RoleDto) => {
    setRole({ id: '', name: `Clone_${roleItem.name}`, permissions: roleItem.permissions })
    setAddRoleModal(true)
  }

  const handleDeleteConfirmation = (roleItem: RoleDto) => {
    setRoleIdToDelete(roleItem.id)
  }

  const handleDelete = async () => {
    if (roleIdToDelete) {
      await identityApiClient.deleteRole(roleIdToDelete)
      setToast(successToast(`Role deleted.`))
      forceRefresh()
      setAddRoleModal(false)
      setRoleIdToDelete(null)
    }
  }

  const sortRoles = (roleDtos: RoleDto[]) => {
    if (!roleDtos) {
      return []
    }
    return roleDtos.sort((a, b) => stringComparator(a.name, b.name))
  }

  const onSubmit = async (values: AddRoleFormValues) => {
    // if (!!values.id) {
    //     await api.updateRole(values.id, values.name, values.permissionIds)
    // } else {
    await identityApiClient.addRole(values.name, values.permissionIds)
    // }
    setToast(successToast(`Role ${values.name} created`))
    forceRefresh()
    setAddRoleModal(false)
  }

  const onSubmitEditRole = async (values: EditRoleFormValues) => {
    if (values.permissionIds.length > 0) {
      await identityApiClient.updateRole(values.id, values.name, values.permissionIds)
      setToast(successToast(`Role ${values.name} updated`))
      forceRefresh()
      setEditRoleModal(false)
    } else {
      setToast(errorToast('You must select at least one permission to update a role.'))
    }
  }

  const onSubmitRolePermissions = async (values: AddRolePermissionFormValues) => {
    await setSecuredPropertiesMap[values.aggregateType](values)
    setToast(successToast(`Permissions for Role ${values.role} set.`))
    forceRefresh()
    setEditRoleModal(false)
  }

  if (isLoading) {
    return <Spinner />
  }

  const copyLatestPermissions = () => {
    let permissionsTs = ''
    permissions.forEach((p) => {
      permissionsTs += `${p.name} = ${p.id},`
    })
    navigator.clipboard.writeText(permissionsTs)
  }

  return (
    <>
      {auditLogModal.visible && auditLogModal.data && (
        <ClientAreaAuditLogModal
          isVisible={auditLogModal.visible}
          onClose={() => setAuditLogModal({ visible: false })}
          roleId={auditLogModal.data.id}
        />
      )}

      <Breadcrumbs crumbs={[{ title: 'Roles', link: links.administrationRoles.url }]} />

      <header>
        <TitleSearch placeholder='Enter role name' onSearch={handleSearch} />
      </header>
      <ButtonGroup>
        <AddButton text='Add role' onClick={addRoleModal} data-test='add-role-button' />
        {isEnabledOnEnvs([appENV.localhost]) && (
          <Button onClick={copyLatestPermissions}>Copy Latest Permissions</Button>
        )}
      </ButtonGroup>
      <Grid
        resizable
        data={roles.slice(dataState.skip, dataState.take + dataState.skip)}
        skip={dataState.skip}
        take={dataState.take}
        sortable={{
          allowUnsort: true,
          mode: 'multiple',
        }}
        total={roles.length}
        onPageChange={pageChange}
        pageable={pageableSettings}
        style={{ zIndex: 0 }}
      >
        {roleColumns
          .filter((c) => c.show !== false)
          .map((column, idx) => (
            <Column key={idx} {...column} />
          ))}
        <Column
          width={50}
          title=''
          sortable={false}
          cell={(props: GridCellProps) => (
            <td>
              <MoreButton
                items={[
                  {
                    name: 'Edit',
                    'data-test': 'edit',
                    onClick: () => handleEdit(props.dataItem),
                  },
                  {
                    name: 'Clone',
                    'data-test': 'clone',
                    onClick: () => handleClone(props.dataItem),
                  },
                  {
                    name: 'Delete',
                    'data-test': 'delete',
                    onClick: () => handleDeleteConfirmation(props.dataItem),
                  },
                  {
                    name: 'Audit log',
                    onClick: () => setAuditLog(props.dataItem),
                  },
                ]}
                data-test='more-button'
              />
            </td>
          )}
        />
      </Grid>
      <ConfirmationModal
        contentText='Are you sure to delete the Role?'
        yesButtonText='Yes, delete'
        onYes={handleDelete}
        onCancel={() => setRoleIdToDelete(null)}
        visible={!!roleIdToDelete}
      />
      {isAddRoleModal && (
        <AddRoleForm
          isOpen={isAddRoleModal}
          onClose={() => setAddRoleModal(false)}
          role={role}
          permissions={permissions}
          onSubmit={onSubmit}
        />
      )}
      {editRoleModal && role && (
        <EditRolePage
          isOpen={editRoleModal}
          onClose={() => setEditRoleModal(false)}
          role={role}
          permissions={permissions}
          tab={EditRoleTab.GENERAL}
          onSubmitRole={onSubmitEditRole}
          onSubmitPermissions={onSubmitRolePermissions}
        />
      )}
    </>
  )
}

export default RolesPage
